import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

// ── Theme hook ───────────────────────────────────────────────────────────────
function useTheme() {
  const stored = localStorage.getItem("lumina-theme");
  const [dark, setDark] = useState(stored === null ? true : stored === "dark");

  useEffect(() => {
    localStorage.setItem("lumina-theme", dark ? "dark" : "light");
    const root = document.documentElement;
    if (dark) {
      root.style.setProperty("--lumina-bg",              "#031427");
      root.style.setProperty("--lumina-surface",         "#102034");
      root.style.setProperty("--lumina-surface-low",     "#0b1c30");
      root.style.setProperty("--lumina-surface-high",    "#1b2b3f");
      root.style.setProperty("--lumina-surface-highest", "#26364a");
      root.style.setProperty("--lumina-on-surface",      "#d3e4fe");
      root.style.setProperty("--lumina-on-surface-var",  "#c6c6cd");
      root.style.setProperty("--lumina-primary",         "#bec6e0");
      root.style.setProperty("--lumina-on-primary",      "#283044");
      root.style.setProperty("--lumina-tertiary",        "#4edea3");
      root.style.setProperty("--lumina-tertiary-bg",     "#001c10");
      root.style.setProperty("--lumina-outline",         "#45464d");
      root.style.setProperty("--lumina-error",           "#ffb4ab");
    } else {
      root.style.setProperty("--lumina-bg",              "#ffffff");
      root.style.setProperty("--lumina-surface",         "#f8fafc");
      root.style.setProperty("--lumina-surface-low",     "#f1f5f9");
      root.style.setProperty("--lumina-surface-high",    "#e2e8f0");
      root.style.setProperty("--lumina-surface-highest", "#cbd5e1");
      root.style.setProperty("--lumina-on-surface",      "#0f172a");
      root.style.setProperty("--lumina-on-surface-var",  "#475569");
      root.style.setProperty("--lumina-primary",         "#6366f1");
      root.style.setProperty("--lumina-on-primary",      "#ffffff");
      root.style.setProperty("--lumina-tertiary",        "#10b981");
      root.style.setProperty("--lumina-tertiary-bg",     "#ecfdf5");
      root.style.setProperty("--lumina-outline",         "#e2e8f0");
      root.style.setProperty("--lumina-error",           "#ef4444");
    }
  }, [dark]);

  const toggle = () => setDark((v) => !v);
  return { dark, toggle };
}

// ── Data ─────────────────────────────────────────────────────────────────────
const lessons = [
  { icon: "play_circle", label: "Intro Video",              time: "15:00", state: "done" },
  { icon: "play_circle", label: "Theory Overview",           time: "25:00", state: "done" },
  { icon: "",             label: "Neural Networks",           time: "Live",  state: "active" },
  { icon: "science",      label: "Practical Lab",             time: "",      state: "locked" },
  { icon: "assignment",   label: "Assignment 1",              time: "",      state: "locked" },
  { icon: "description",  label: "Reading Material",          time: "",      state: "locked" },
];

const modules = [
  { title: "Module 2: Advanced Theory", note: "70% Progress Required" },
  { title: "Module 3: Practical Lab" },
  { title: "Module 4: Case Studies" },
  { title: "Module 5: Final Exam" },
];

const learnItems = [
  "Architect neural networks from scratch using Python and NumPy.",
  "Implement backpropagation and gradient descent optimization.",
  "Evaluate model performance using industry-standard metrics.",
  "Deploy scalable models for real-world production environments.",
];

const tags = [
  { icon: "history_edu", label: "Theoretical Basis" },
  { icon: "code",        label: "Python/NumPy" },
  { icon: "data_object", label: "Linear Algebra" },
];

const assets = [
  { name: "Lecture_Slides.pdf",    size: "4.2 MB" },
  { name: "Architecture.pptx",     size: "12.8 MB" },
];

// ── Sub-components ───────────────────────────────────────────────────────────

function Tab({ label, active }) {
  return (
    <button
      className={`px-6 py-3 font-medium transition-colors whitespace-nowrap border-b-2 ${
        active
          ? "font-bold text-lumina-primary border-lumina-tertiary"
          : "border-transparent text-lumina-on-surface-var"
      }`}
    >
      {label}
    </button>
  );
}

function DownloadRow({ name, size }) {
  const [done, setDone] = useState(false);
  return (
    <div
      onClick={() => { setDone(true); setTimeout(() => setDone(false), 2000); }}
      className="flex items-center gap-3 rounded-lg py-3 px-3 cursor-pointer transition-colors bg-lumina-surface-low border border-lumina-outline hover:bg-lumina-surface-high"
    >
      <div className="flex-1">
        <div className="text-sm font-semibold text-lumina-on-surface">{name}</div>
        <div className="text-xs text-lumina-on-surface-var">{size}</div>
      </div>
      <div className="w-[30px] h-[30px] rounded-lg flex items-center justify-center shrink-0 bg-lumina-tertiary-bg text-lumina-tertiary">
        <span className="material-symbols-outlined text-lg">{done ? "check" : "download"}</span>
      </div>
    </div>
  );
}

function LessonRow({ icon, label, time, state }) {
  if (state === "active") {
    return (
      <div className="flex items-center gap-3 py-3 pl-10 pr-4 cursor-pointer border-l-4 bg-lumina-surface-highest border-l-lumina-tertiary">
        <div className="w-5 h-5 rounded-full border-2 flex items-center justify-center border-lumina-tertiary">
          <div className="w-2 h-2 rounded-full bg-lumina-tertiary" />
        </div>
        <span className="text-sm font-bold flex-1 text-lumina-primary">{label}</span>
        <span className="text-[11px] font-bold animate-pulse text-lumina-tertiary">{time}</span>
      </div>
    );
  }
  if (state === "done") {
    return (
      <div className="flex items-center gap-3 py-3 pl-10 pr-4 cursor-pointer border-l-4 border-l-lumina-tertiary transition-colors hover:bg-lumina-surface-high">
        <span className="material-symbols-outlined text-xl text-lumina-on-surface-var">{icon}</span>
        <span className="text-sm font-semibold flex-1 text-lumina-on-surface">{label}</span>
        <span className="text-xs text-lumina-on-surface-var">{time}</span>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-3 py-3 pl-10 pr-4 cursor-pointer border-l-4 border-transparent transition-colors hover:bg-lumina-surface-high">
      <span className="material-symbols-outlined text-xl text-lumina-on-surface-var">{icon}</span>
      <span className="text-sm flex-1 text-lumina-on-surface-var">{label}</span>
    </div>
  );
}

function Bullet({ text }) {
  return (
    <li className="flex items-center gap-3">
      <span className="w-1.5 h-1.5 rounded-full bg-lumina-primary" />
      <span className="text-sm text-lumina-on-surface-var">{text}</span>
    </li>
  );
}

// ── Main component ───────────────────────────────────────────────────────────
export default function CourseDetail() {
  const navigate = useNavigate();
  const { dark, toggle } = useTheme();

  return (
    <div className="min-h-screen bg-lumina-bg text-lumina-on-surface font-sans">
      {/* ── TOP NAV ────────────────────────────────────────────── */}
      <header className="w-full h-16 fixed top-0 z-50 border-b bg-lumina-surface border-lumina-outline">
        <div className="flex justify-between items-center px-2 max-w-full mx-auto h-full">
          <div className="flex items-center gap-12">
            <span className="text-2xl font-bold text-lumina-primary">
              Fillop
            </span>
            <nav className="hidden md:flex items-center gap-8">
              <a href="#" className="text-sm font-medium pb-1 text-lumina-primary">
                My Courses
              </a>
              <a href="#" className="text-sm font-medium transition-colors pb-1 text-lumina-on-surface-var">
                Resources
              </a>
              <a href="#" className="text-sm font-medium transition-colors pb-1 text-lumina-on-surface-var">
                Certificates
              </a>
            </nav>
          </div>

          <div className="flex items-center gap-4">
            {/* Dark/Light toggle icon */}
            <button
              onClick={toggle}
              className="w-9 h-9 rounded-xl flex items-center justify-center transition-colors bg-lumina-surface-high"
              aria-label="Toggle theme"
            >
              <span className="material-symbols-outlined text-lg text-lumina-on-surface-var">
                {dark ? "light_mode" : "dark_mode"}
              </span>
            </button>

            <button className="material-symbols-outlined transition-colors text-lumina-on-surface-var">
              notifications
            </button>
            <button className="material-symbols-outlined transition-colors text-lumina-on-surface-var">
              settings
            </button>
            <div className="w-8 h-8 rounded-full overflow-hidden border bg-lumina-surface-highest border-lumina-outline">
              <img
                alt="User"
                className="w-full h-full object-cover"
                src="https://lh3.googleusercontent.com/aida-public/AB6AXuDnOxGEI652tuLo_2GajL0aVyJ4X3zIb6xhN6XrPg-kRrkk-v2Bswt5nba9lU4EtLxg1pkJQYp6xo_r6W-w8g8jeyOoxN3KRjcGhWlDaD32NvjM7yJoRv0gozQVM6O4D13g9vuCpJRfe3SyxZQHJRQTsRLILRjebEfpEPmX4X1WU89Ma2zXOiQUH95M6Q_6xnLrNo80RPq_pE8bKH57FI2KI-hBAQdkl90zlhtiLXhmH6SadJDZ0ibyXVcrgBhd7nlbWjIFyH3M1Sg"
              />
            </div>
          </div>
        </div>
      </header>

      {/* ── MAIN LAYOUT ────────────────────────────────────────── */}
      <main className="pt-16 min-h-screen flex">
        {/* ── Main content area ────────────────────────────────── */}
        <div className="flex-1 flex flex-col items-center custom-scrollbar overflow-y-auto mr-0 lg:mr-[280px]">
          <div className="w-full max-full px-8 py-2 space-y-10">

            {/* Breadcrumbs + title */}
            <div className="flex justify-between items-end flex-wrap gap-4">
              <div>
                <div className="flex items-center justify-center gap-4 text-sm mb-4 text-lumina-on-surface-var">
                  {/* Back to Home */}
                  <button
                    onClick={() => navigate("/")}
                    className="flex items-center gap-2 text-lg font-medium transition-colors mb-2 border px-2 text-lumina-on-surface-var"
                  >
                    <span className="material-symbols-outlined text-bolder">arrow_back</span>
                  </button>
                  <span>Machine Learning Specialization</span>
                  <span className="material-symbols-outlined text-sm">chevron_right</span>
                  <span className="text-lumina-primary">Module 1: Fundamentals</span>
                </div>
                <h1 className="text-3xl font-semibold tracking-tight text-lumina-on-surface">
                  Neural Network Foundations
                </h1>
                <p className="text-sm mt-2 text-lumina-on-surface-var">
                  Course Overview: 15 Videos, 5 Assessments, 4 Practical Labs
                </p>
              </div>
              <span className="text-xs font-medium px-3 py-1 rounded-full text-lumina-tertiary bg-lumina-tertiary-bg">
                IN PROGRESS
              </span>
            </div>

            {/* ── Grid: left (2/3) + right (1/3) ──────────────── */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">

              {/* ── LEFT 2/3 ──────────────────────────────────── */}
              <div className="lg:col-span-2 space-y-10">

                {/* Tabs */}
                <div className="flex border-b border-lumina-outline">
                  <Tab label="Overview"   active />
                  <Tab label="Resources" />
                  <Tab label="Discussion" />
                  <Tab label="Notes" />
                </div>

                <div className="space-y-8">

                  {/* Description */}
                  <div>
                    <h2 className="text-xl font-semibold mb-4 text-lumina-on-surface">
                      Course Introduction & Overview
                    </h2>
                    <div className="space-y-4 text-base leading-relaxed text-lumina-on-surface-var">
                      <p>
                        Machine Learning has emerged as the defining technology of the 21st century,
                        powering everything from personalised recommendations to autonomous vehicles
                        and medical breakthroughs.
                      </p>
                      <p>
                        This specialisation is built upon three core pillars: establishing rigorous
                        mathematical Foundations, exploring Advanced Theory in deep learning
                        architectures, and providing extensive Hands-on Application through real-world
                        datasets.
                      </p>
                      <p>
                        Our learning philosophy is simple: Mastery through practice and exploration.
                        We encourage you to experiment with parameters, break code, and rebuild models
                        from the ground up.
                      </p>
                    </div>
                  </div>

                  {/* Video preview */}
                  <div
                    className="relative w-full aspect-video rounded-2xl overflow-hidden group cursor-pointer"
                    style={{
                      backgroundColor: "var(--lumina-surface-highest)",
                      border: "1px solid color-mix(in srgb, var(--lumina-outline) 20%, transparent)",
                      boxShadow: "0 0 40px -10px color-mix(in srgb, var(--lumina-tertiary) 20%, transparent)",
                    }}
                  >
                    <img
                      alt="Neural Network Visualization"
                      className="w-full h-full object-cover opacity-60 group-hover:scale-105 transition-transform duration-700"
                      src="https://images.unsplash.com/photo-1677442136019-21780ecad995?auto=format&fit=crop&q=80&w=1000"
                    />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div
                        className="w-20 h-20 rounded-full flex items-center justify-center backdrop-blur-sm group-hover:scale-110 transition-all duration-300"
                        style={{
                          backgroundColor: "color-mix(in srgb, var(--lumina-primary) 20%, transparent)",
                          border: "1px solid color-mix(in srgb, var(--lumina-primary) 27%, transparent)",
                        }}
                      >
                        <div className="w-16 h-16 rounded-full flex items-center justify-center shadow-lg bg-lumina-primary text-lumina-on-primary">
                          <span className="material-symbols-outlined text-[40px]" style={{ fontVariationSettings: "'FILL' 1" }}>play_arrow</span>
                        </div>
                      </div>
                    </div>
                    <div
                      className="absolute bottom-0 left-0 right-0 p-6"
                      style={{ background: "linear-gradient(to top, color-mix(in srgb, var(--lumina-bg) 90%, transparent), transparent)" }}
                    >
                      <div className="flex items-center gap-3">
                        <span
                          className="px-2 py-0.5 text-[10px] font-bold rounded uppercase tracking-widest"
                          style={{
                            color: "var(--lumina-tertiary)",
                            backgroundColor: "color-mix(in srgb, var(--lumina-tertiary) 20%, transparent)",
                          }}
                        >
                          Preview
                        </span>
                        <span className="text-sm font-semibold text-lumina-on-surface">
                          Introduction to Neural Architectures
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Working Examples */}
                  <div className="space-y-5">
                    <h3 className="text-lg font-semibold text-lumina-on-surface">
                      Working Examples
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {learnItems.map((item) => (
                        <div
                          key={item}
                          className="flex items-start gap-3 p-4 rounded-xl transition-colors bg-lumina-surface-low"
                          style={{ border: "1px solid color-mix(in srgb, var(--lumina-outline) 27%, transparent)" }}
                        >
                          <span className="material-symbols-outlined shrink-0 mt-0.5 text-lumina-tertiary">
                            check_circle
                          </span>
                          <span className="text-sm leading-relaxed text-lumina-on-surface-var">
                            {item}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Tags */}
                  <div className="flex flex-wrap gap-3">
                    {tags.map((tag) => (
                      <span
                        key={tag.label}
                        className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm bg-lumina-surface-high text-lumina-on-surface-var"
                      >
                        <span className="material-symbols-outlined text-xl text-lumina-tertiary">
                          {tag.icon}
                        </span>
                        {tag.label}
                      </span>
                    ))}
                  </div>

                  {/* Prerequisites */}
                  <div
                    className="p-5 rounded-xl border"
                    style={{
                      backgroundColor: "color-mix(in srgb, var(--lumina-primary) 5%, transparent)",
                      borderColor: "color-mix(in srgb, var(--lumina-primary) 20%, transparent)",
                    }}
                  >
                    <div className="flex items-center gap-2 mb-3 text-lumina-primary">
                      <span className="material-symbols-outlined">info</span>
                      <h4 className="font-bold">Prerequisites</h4>
                    </div>
                    <ul className="space-y-2">
                      <Bullet text="Basic proficiency in Python programming." />
                      <Bullet text="Foundational understanding of Linear Algebra and Calculus." />
                    </ul>
                  </div>

                  {/* Prev / Next navigation */}
                  <div className="flex items-center justify-between pt-4 border-t border-lumina-outline">
                    <button className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors text-lumina-on-surface-var border border-lumina-outline bg-lumina-surface-low hover:bg-lumina-surface-high">
                      <span className="material-symbols-outlined text-base">chevron_left</span>
                      Previous Lesson
                    </button>

                    <button className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-bold transition-colors text-lumina-on-primary bg-lumina-primary hover:bg-lumina-on-surface-var">
                      Next Lesson
                      <span className="material-symbols-outlined text-base">chevron_right</span>
                    </button>
                  </div>

                </div>
              </div>

              {/* ── RIGHT 1/3: Lesson Assets ──────────────────── */}
              <div className="space-y-6">
                <h3 className="text-sm uppercase tracking-widest font-bold text-lumina-primary">
                  Lesson Assets
                </h3>
                <div className="space-y-4">
                  {assets.map((a) => (
                    <DownloadRow key={a.name} name={a.name} size={a.size} />
                  ))}
                </div>

                <div
                  className="p-1 rounded-sm border-l-4"
                  style={{
                    backgroundColor: "color-mix(in srgb, var(--lumina-tertiary-bg) 27%, transparent)",
                    borderColor: "var(--lumina-tertiary)",
                    borderLeftColor: "var(--lumina-tertiary)",
                  }}
                >
                  <h4 className="font-bold mb-2 text-lumina-tertiary">Instructor Note</h4>
                  <p className="text-sm leading-relaxed text-lumina-on-surface-var">
                    Please ensure you have completed the Linear Algebra refresher before starting
                    the Practical Lab.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── RIGHT SIDEBAR ─────────────────────────────────────── */}
        <aside className="hidden lg:flex flex-col fixed right-0 top-16 h-[calc(100vh-64px)] w-[280px] bg-lumina-surface-low border-l border-lumina-outline">
          {/* Sidebar header */}
          <div className="p-4 border-b border-lumina-outline">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-sm font-bold text-lumina-on-surface">Course Syllabus</h2>
              <span className="text-xs font-bold px-2 py-0.5 rounded text-lumina-tertiary bg-lumina-tertiary-bg">
                2/15 Done
              </span>
            </div>

            <div className="space-y-2">
              <div className="flex justify-between text-[11px] uppercase font-bold tracking-wider text-lumina-on-surface-var">
                <span>Overall Progress</span>
                <span className="text-lumina-tertiary">15%</span>
              </div>
              <div className="w-full h-1.5 rounded-full overflow-hidden bg-lumina-surface-highest">
                <div className="h-full rounded-full transition-all duration-700 bg-lumina-tertiary" style={{ width: "15%" }} />
              </div>
            </div>

            <button className="w-full mt-6 py-2.5 text-sm font-bold rounded-lg flex items-center justify-center gap-2 transition-colors active:scale-95 duration-150 bg-lumina-on-surface text-lumina-bg">
              <span className="material-symbols-outlined text-lg">fullscreen</span>
              Focus Mode
            </button>
          </div>

          {/* Syllabus */}
          <div className="flex-1 overflow-y-auto custom-scrollbar">
            {/* Module 1 */}
            <div className="border-b" style={{ borderColor: "color-mix(in srgb, var(--lumina-outline) 27%, transparent)" }}>
              <div className="flex items-center gap-3 p-4 border-l-4 cursor-pointer bg-lumina-surface-highest border-l-lumina-tertiary">
                <span className="material-symbols-outlined text-lumina-tertiary">play_circle</span>
                <div className="flex-1">
                  <p className="text-sm font-bold text-lumina-on-surface">Module 1: Fundamentals</p>
                </div>
                <span className="material-symbols-outlined text-lumina-on-surface-var">keyboard_arrow_up</span>
              </div>
              <div style={{ backgroundColor: "color-mix(in srgb, var(--lumina-surface-low) 7%, transparent)" }}>
                {lessons.map((l) => (
                  <LessonRow key={l.label} {...l} />
                ))}
              </div>
            </div>

            {modules.map((m) => (
              <div key={m.title} className="border-b opacity-60" style={{ borderColor: "color-mix(in srgb, var(--lumina-outline) 27%, transparent)" }}>
                <div className="flex items-center gap-3 p-4 cursor-not-allowed" style={{ borderLeft: "4px solid transparent" }}>
                  <span className="material-symbols-outlined text-lumina-on-surface-var">lock</span>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-lumina-on-surface-var">{m.title}</p>
                    {m.note && <p className="text-[11px] font-bold text-lumina-error">{m.note}</p>}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Sidebar footer */}
          <div className="p-4 border-t bg-lumina-surface border-lumina-outline">
            <button className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium transition-colors text-lumina-on-surface-var bg-lumina-surface-high hover:bg-lumina-surface-highest">
              <span className="material-symbols-outlined text-base">logout</span>
              <span>Log Out</span>
            </button>
          </div>
        </aside>
      </main>
    </div>
  );
}